import {
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Directive,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { ISelectInput } from '@portal/shared/ui/filter/src/lib/box/interfaces/select-input.interface';
import { FilterContentItem } from '@portal/shared/ui/filter/src/lib/content/item';
import { SearchOperator } from '@portal/shared/ui/filter/src/lib/search/enums/search-operator.enum';
import { IFilterToApply } from '@portal/shared/ui/filter/src/lib/search/interfaces/filter-to-apply.interface';
import { ISearchCriteria } from '@portal/shared/ui/filter/src/lib/search/interfaces/search-criteria.interface';
import { cloneDeep, filter, forEach } from 'lodash';
import { of, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FilterCheckboxComponent } from './checkbox/search-checkbox.component';
import { SwitchToggleComponent } from './switch-toggle/switch-toggle.component';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class FilterBase extends FilterContentItem implements OnInit, OnDestroy, AfterViewInit {
  @Output()
  filterUpdated: EventEmitter<IFilterToApply> = new EventEmitter();

  @Output()
  staticFilterUpdated: EventEmitter<IFilterToApply> = new EventEmitter();

  @Output()
  filterApplied: EventEmitter<IFilterToApply> = new EventEmitter();

  @Input()
  selectedEntities: Map<string, ISearchCriteria>;

  @Input()
  entityRemoved$ = new Subject<ISelectInput>();

  @Input()
  closeSubmenus$ = new Subject<boolean>();

  @ViewChild('preSelectedEntities') preSelectedEntities: FilterCheckboxComponent;

  @ViewChild('switchToggle') switchToggle: SwitchToggleComponent;

  searchSelector: string;

  itemToggled: EventEmitter<number> = new EventEmitter();
  isOpen = false;

  localPreSelectedEntities: Map<string, ISelectInput> = new Map();

  showSelectedEntities: Map<string, ISelectInput> = new Map();

  entities: ISelectInput[];

  searchedText: string;

  staticListUpdateSubject$ = new Subject<IFilterToApply>();
  staticListUpdateSub: Subscription;

  backToMenuSubject$ = new Subject<void>();
  backToMenuSub: Subscription;

  private allEntities: ISelectInput[];

  constructor(selector: string = '', list: ISelectInput[] = []) {
    super();
    this.searchSelector = selector;
    this.entities = list;
    this.allEntities = cloneDeep(this.entities);
  }

  ngAfterViewInit(): void {
    this.updateSwitchToggleState();
  }

  ngOnInit(): void {
    if (!this.selectedEntities) {
      this.selectedEntities = new Map();
    }

    const entities: ISearchCriteria = cloneDeep(this.selectedEntities.get(this.searchSelector));

    this.staticListUpdateSub = this.staticListUpdateSubject$
      .pipe(debounceTime(100))
      .subscribe(() => {
        this.staticFilterUpdated.emit({
          key: this.searchSelector,
          value: Array.from(this.localPreSelectedEntities.values()),
        });
      });

    this.backToMenuSub = this.backToMenuSubject$.pipe(debounceTime(100)).subscribe(() => {
      const element = document.getElementById('add-filter');
      if (element) {
        element.click();
      }
    });

    if (entities) {
      this.setSelectedEntities(entities);
    }

    this.entityRemoved$.subscribe((entityToRemove: ISelectInput) => {
      const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
      if (selectedEntity) {
        this.onEntityRemoved(entityToRemove);
        this.onFilterApplied();
        this.itemToggled.emit();
      }
    });

    this.closeSubmenus$.subscribe((close: boolean) => {
      if (close && this.isOpen) {
        this.itemToggled.emit();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.staticListUpdateSub) {
      this.staticListUpdateSub.unsubscribe();
    }

    if (this.backToMenuSub) {
      this.backToMenuSub.unsubscribe();
    }
  }

  setSelectedEntities(value: ISearchCriteria): void {
    // Set selectedFilters
    const values = new Set<string>(value.argument.replace('(', '').replace(')', '').split(','));
    forEach(Array.from(values), (argument: string) => {
      const textInput = this.entities.find((entity: ISelectInput) => {
        return entity.id === argument;
      });
      this.localPreSelectedEntities.set(argument, {
        id: argument,
        text: textInput ? textInput.text : '',
      });
    });

    this.staticListUpdateSubject$.next();

    this.updateEntities();
  }

  onSearch(searchValue: string): void {
    if (!searchValue) {
      this.entities = this.allEntities;
      this.searchedText = null;
    } else {
      this.entities = this.allEntities;
      this.searchedText = searchValue;
      this.entities = filter(this.entities, (entity: ISelectInput) => {
        return entity.text.toLowerCase().includes(searchValue.toLowerCase());
      });
    }
    this.updateEntities();
  }

  updateEntities(): void {
    this.showSelectedEntities = cloneDeep(this.localPreSelectedEntities);
    const entitiesToExclude = Array.from(this.localPreSelectedEntities.keys());
    this.entities = filter(this.allEntities, (entity: ISelectInput) => {
      return !entitiesToExclude.includes(entity.id);
    });
  }

  onEntityRemoved(selectedEntity: ISelectInput): void {
    this.localPreSelectedEntities.delete(selectedEntity.id);
    this.updateSwitchToggleState();
  }

  onEntityAdded(selectedEntity: ISelectInput): void {
    this.localPreSelectedEntities.set(selectedEntity.id, selectedEntity);
    this.updateSwitchToggleState();
  }

  onFilterApplied(): void {
    this.updateEntities();

    const values: string[] = this.getValues();
    if (values.length) {
      this.filterUpdated.emit({
        key: this.searchSelector,
        operator: SearchOperator.In,
        value: Array.from(this.localPreSelectedEntities.values()),
        argument: `(${Array.from(values).join(',')})`,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        value: Array.from(this.localPreSelectedEntities.values()),
      });
    } else {
      this.filterUpdated.emit({
        key: this.searchSelector,
        toRemove: true,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        toRemove: true,
      });
    }

    this.filterApplied.emit();
    this.itemToggled.emit();
  }

  getList(): Observable<ISelectInput[]> {
    return of(this.entities);
  }

  getValues(): string[] {
    const values = new Set<string>();
    Array.from(this.localPreSelectedEntities.values()).forEach((val: ISelectInput) => {
      values.add(val.id);
    });

    return Array.from(values);
  }

  backToMenu(): void {
    this.itemToggled.emit();
    this.backToMenuSubject$.next();
  }

  openContent(): void {
    this.itemToggled.emit();
    this.onSearch('');
  }

  onSelectAllSwitchToggle(): void {
    if (this.switchToggle.switchOn) {
      Array.from(this.entities).forEach((entityToSelect) => {
        if (!entityToSelect.disabled) {
          this.localPreSelectedEntities.set(entityToSelect.id, entityToSelect);
        }
      });
      if (this.showSelectedEntities.size > 0) {
        this.showSelectedEntities.forEach((selectedCheckbox) => {
          if (!this.preSelectedEntities.isEntitySelected(selectedCheckbox.id)) {
            this.preSelectedEntities.onCheckboxChecked(selectedCheckbox.id, selectedCheckbox.text);
          }
        });
      }
    } else if (this.localPreSelectedEntities.size > 0) {
      this.localPreSelectedEntities.clear();
    }
  }

  onSelectAll(): void {
    Array.from(this.entities).forEach((entityToSelect) => {
      if (!entityToSelect.disabled) {
        this.localPreSelectedEntities.set(entityToSelect.id, entityToSelect);
      }
    });
    if (this.showSelectedEntities.size > 0) {
      this.showSelectedEntities.forEach((selectedCheckbox) => {
        if (!this.preSelectedEntities.isEntitySelected(selectedCheckbox.id)) {
          this.preSelectedEntities.onCheckboxChecked(selectedCheckbox.id, selectedCheckbox.text);
        }
      });
    }
  }

  updateSwitchToggleState(): void {
    if (this.switchToggle) {
      this.switchToggle.switchOn =
        this.localPreSelectedEntities.size ===
        this.allEntities.filter((entities) => !entities.disabled).length;
    }
  }

  updateListEntity(list: ISelectInput[]): void {
    this.entities = list;
    this.allEntities = cloneDeep(this.entities);
  }
}
